import React from 'react'
import { ClassNames, DateRange, DayPicker } from 'react-day-picker'
import { Popover } from '../../layouts/components/popover'
import { Button, ButtonStyle } from '../simple/button'
import { differenceInCalendarDays, endOfDay, startOfDay, subDays } from 'date-fns'
import { useTranslation } from '../../providers/translation'
import { Select } from './select'
import { IconSort } from '../../icons/library'
import { pluralize } from '../../utils'

const classNames: Partial<ClassNames> = {
  day_button: `flex items-center justify-center w-8 h-8 rounded hover:bg-[#586f9112]`,
  selected: `*:!bg-link-500 *:text-white *:hover:bg-link-500 *:hover:text-white`,
  range_start: '!rounded-l rounded-r-none bg-link-500 text-white',
  range_middle: 'bg-link-500 text-white',
  range_end: '!rounded-r rounded-l-none bg-link-500 text-white',
  month_caption: 'flex align-center justify-center h-10 mt-1',
  weekday: 'text-sm opacity-75 pb-2 font-normal',
  nav: 'absolute left-0 right-0 -top-2 flex justify-between items-center h-10 px-2',
  button_previous: 'scale-75 opacity-75',
  button_next: 'scale-75 opacity-75',
  today: '*:bg-link-500/10',
  outside: 'text-gray-400',
  months: 'relative',
  disabled: 'opacity-50 cursor-not-allowed',
}

/**
 * Input for selecting a single date. Internally uses react-day-picker.
 * @params selected - The selected date
 * @params onSelect - The function to call when the date is selected
 *
 * Many other props, see react-day-picker documentation
 */
export const DatePicker: React.FC<React.ComponentPropsWithoutRef<typeof DayPicker>> = (props) => {
  return <DayPicker classNames={classNames} {...props} />
}

interface PopupDateRangePickerProps {
  children: React.ReactNode
  range?: DateRange
  onRangeChange?: (range: DateRange | undefined) => void
  includeLongRanges?: boolean
}

/**
 * Date range picker that appears in a popup when the trigger is clicked. It allows the user to select a date range from a list of predefined ranges or by selecting dates on a calendar.
 * @param children - The trigger element for the popup
 * @param range - The current date range
 * @param onRangeChange - The function to call when the date range changes
 * @param includeLongRanges - Whether to include long ranges (6 months and 1 year) in the suggestions
 */
export const PopupDateRangePicker: React.FC<PopupDateRangePickerProps> = ({
  children,
  range,
  onRangeChange,
  includeLongRanges,
}) => {
  const [open, setOpen] = React.useState(false)
  const [internalRange, setInternalRange] = React.useState<DateRange | undefined>(undefined)

  React.useEffect(() => {
    if (range !== internalRange) setInternalRange(range)
  }, [range])

  const { tr } = useTranslation()

  const onPopupOpenChange = (open: boolean) => {
    if (!open && range !== internalRange) onRangeChange?.(internalRange)
    setOpen(open)
  }

  const setAndClose = (range?: DateRange) => {
    setInternalRange(range)
    onRangeChange?.(range)
    setOpen(false)
  }

  const ranges = [
    {
      name: tr('Today'),
      range: {
        from: startOfDay(new Date()),
        to: new Date(),
      },
    },
    {
      name: tr('Yesterday'),
      range: {
        from: startOfDay(subDays(new Date(), 1)),
        to: endOfDay(subDays(new Date(), 1)),
      },
    },
    {
      name: tr('Last 7 Days'),
      range: {
        from: startOfDay(subDays(new Date(), 6)),
        to: new Date(),
      },
    },
    {
      name: tr('Last 30 Days'),
      range: {
        from: startOfDay(subDays(new Date(), 29)),
        to: new Date(),
      },
    },
    ...(includeLongRanges
      ? [
          {
            name: tr('Last 6 Months'),
            range: {
              from: startOfDay(subDays(new Date(), 179)),
              to: new Date(),
            },
          },
          {
            name: tr('Last Year'),
            range: {
              from: startOfDay(subDays(new Date(), 364)),
              to: new Date(),
            },
          },
        ]
      : []),
  ]

  return (
    <Popover
      onOpenChange={onPopupOpenChange}
      open={open}
      trigger={children}
      onOpenAutoFocus={(e) => e.preventDefault()}
    >
      <div className="flex">
        <div className="flex flex-col gap-2 p-2">
          {ranges.map(({ name, range }) => (
            <Button key={name} buttonStyle={ButtonStyle.ghost} onClick={() => setAndClose(range)} small>
              {name}
            </Button>
          ))}
          <Button buttonStyle={ButtonStyle.ghostLogoTransparent} onClick={() => setAndClose()} small>
            {tr('Clear')}
          </Button>
        </div>
        <DatePicker
          required
          mode="range"
          selected={internalRange}
          onSelect={(date) => setInternalRange(date)}
          className="m-2"
        />
      </div>
    </Popover>
  )
}

export type SimpleDateRange = DateRange | 'month' | 'schoolyear' | 'all'
export const simpleRangeOptions = [
  {
    name: 'This Month',
    value: 'month',
  },
  {
    name: 'School Year',
    value: 'schoolyear',
  },
  {
    name: 'All Time',
    value: 'all',
  },
]

/**
 * Simple date range picker that allows the user to select a date range from a list of predefined ranges (month, school year, all). If they choose custom, a calendar will appear to select the range.
 * @param range - The current date range
 * @param onRangeChange - The function to call when the date range changes. This is either called with a DateRange or 'all', 'month', or 'schoolyear'
 * @param arrow - The arrow icon to display in the select
 * @param className - Additional classes to apply to the select
 * @param outerClasses - Additional classes to apply to the outer container
 */
export const SimpleDateRangePicker: React.FC<{
  range: SimpleDateRange
  onRangeChange: (range: SimpleDateRange) => void
  arrow?: React.ReactNode
  className?: string
  outerClasses?: string
}> = ({ range, onRangeChange, className, outerClasses, arrow = <IconSort /> }) => {
  const existingCustomRange = typeof range === 'object' ? range : undefined
  const rangeDays =
    existingCustomRange && existingCustomRange.to && existingCustomRange.from
      ? differenceInCalendarDays(existingCustomRange.to, existingCustomRange.from) + 1
      : undefined

  const customRangeOption = {
    name: rangeDays ? `${rangeDays} ${pluralize(rangeDays, 'Day')}` : 'Custom',
    value: 'custom',
  }
  const allRangeOptions = [...simpleRangeOptions, customRangeOption]
  const selectedRange = simpleRangeOptions.find((r) => r.value === range) || customRangeOption
  const [showCustomRangePicker, setShowCustomRangePicker] = React.useState(false)

  return (
    <Popover
      onOpenChange={(open) => {
        if (open) return // only open if the custom option is selected, not by clicking trigger
        setShowCustomRangePicker(false)
      }}
      open={showCustomRangePicker}
      trigger={
        <Select
          className={`${className} min-w-28 pr-6 text-right`}
          outerClasses={outerClasses}
          small
          arrow={arrow}
          value={showCustomRangePicker ? customRangeOption : selectedRange}
          onChange={(range) => {
            if (['month', 'schoolyear', 'all'].includes(range.value)) {
              onRangeChange(range.value as SimpleDateRange)
              setShowCustomRangePicker(false)
            } else {
              setShowCustomRangePicker(true)
            }
          }}
          onOpen={(open) => {
            if (open && existingCustomRange) setShowCustomRangePicker(true)
          }}
          options={allRangeOptions}
        />
      }
    >
      <DatePicker
        required
        mode="range"
        selected={existingCustomRange}
        onSelect={(date) => {
          onRangeChange(date)
        }}
        className="m-2"
      />
    </Popover>
  )
}

export const formatDateRange = (range: SimpleDateRange) => {
  if (typeof range === 'string') return range
  if (!range.from || !range.to) return 'month' // I dont think this should happen
  return `${range.from.toLocaleDateString()}-${range.to.toLocaleDateString()}`
}
